<template>
  <div class="fabric-roll">
    <div class="grid bg-white" v-if="!print">
      <div class="grid-title">
        <div class="row">
          <div class="col-md-6">
              <h4 class="text-bold">Fabric Inventory
                <span class="fabric-calculation-info-container bg-primary">
                <FabricCalculationInfo>
                  <template #title>This table shows the Fabric Inventory.</template>
                  <template #content>
                      <div class="calculation-info-container">
                          <div class="title">Total Stock</div>
                          <div class="description">The total length of fabric available in stock.</div>
                          <code>
                              if($unit = 'y')<br>
                              $result =  $lengthRemaining * 0.9144;<br>
                              else<br>
                              $result =  $lengthRemaining;
                          </code>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">Reserved Stock</div>
                          <div class="description">The total length of fabric reserved for shirt orders.</div>
                          <code>
                              $totalFabricCutLength = sum($fabricCutLengthForEachOrder); <br/>
                              $result = $totalFabricCutLength;
                          </code>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">Free Stock</div>
                          <div class="description">The total length of fabric available for new orders.</div>
                          <code>
                              $result = $totalStock - $reservedStock;
                          </code>
                      </div>
                      <div class="calculation-info-container">
                        <div class="title">Sold Out Date</div>
                        <div class="description">Estimates when fabric stock will run out based on current usage and availability.</div>
                        <code>
                          if($fabricNotActiveFor30days && $fabricNotActiveFor60days)<br>
                          $result = 'No Data';<br>
                          <br>
                          if($availableStock == 0 && $future == 'keep')<br>
                          $result = Today's date;<br>
                          <br>
                          if($fabricStatus == 'sold out coming back')<br>
                          $result = Today's date;<br>
                          <br>
                          $maximumAverageForStock = max($averageUsageIn30days, $averageUsageIn60days, $averageSaleIn30days, $averageSaleIn60days); <br>
                          $lengthUntilSoldOutTriggered = 30;<br>
                          <br>
                          $daysLeftForStock = ($remainingStock - $lengthUntilSoldOutTriggered) / $maximumAverageForStock;<br>
                          if($daysLeftForStock > 0)<br>
                          <br>
                          if($average == $averageUsage30days)<br>
                          $usedCalculationValue = 'U/30';<br>
                          if($average == $averageUsage60days)<br>
                          $usedCalculationValue = 'U/60';<br>
                          if($average == $averageSale30days)<br>
                          $usedCalculationValue = 'S/30';<br>
                          if($average == $averageSale60days)<br>
                          $usedCalculationValue = 'S/60';<br>
                          <br>
                          $result = Today's date + $daysLeftForStock + $usedCalculationValue;<br>
                          <br>
                          else<br>
                          $result = 'No Data';<br>
                        </code>
                      </div>
                      <div class="calculation-info-container">
                        <div class="title">Order Necessary</div>
                        <div class="description">Estimates if fabric is necessary for order based on current usage and availability.</div>
                        <code>
                          if($fabricNotActiveFor30days && $fabricNotActiveFor60days)<br>
                          $result = 'No';<br>
                          <br>
                          if($availableStock == 0 && $future == 'keep')<br>
                          $result = 'Yes';<br>
                          <br>
                          if($fabricStatus == 'sold out coming back')<br>
                          $result = 'Yes';<br>
                          <br>
                          $maximumAverageForStock = max($averageUsageIn30days, $averageUsageIn60days, $averageSaleIn30days, $averageSaleIn60days); <br>
                          $lengthUntilSoldOutTriggered = 30;<br>
                          <br>
                          $daysLeftForStock = ($remainingStock - $lengthUntilSoldOutTriggered) / $maximumAverageForStock;<br>
                          $supplierLeadTime = $supplierEfficiency * 7 (+ 20% margin);<br>
                          if ($future == 'keep' && $daysLeftForStock <= $supplierLeadTime)<br>
                          $result = 'Yes';<br>
                          <br>
                          else<br>
                          $result = 'No';<br>
                        </code>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">Placed Order</div>
                          <div class="description">Indicates if an order has been placed for the fabric.</div>
                          <code>
                              if($amountOrdered > $amountDelivered)<br>
                              $result = 'Yes'<br>
                              <br>
                              else<br>
                              $result = 'No'
                          </code>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">ETA</div>
                          <div class="description">The estimated date when the ordered fabric will be delivered.</div>
                          <code>
                              $result = min($fabricOrderCreatedDate + $supplierLeadTime);
                          </code>
                      </div>
                  </template>
                </FabricCalculationInfo>
              </span>
              </h4>

          </div>
          <div class="col-md-6 text-right">
            <button class="btn btn-secondary mb-2" @click="showPrintView()">
              <i class="fa fa-print" aria-hidden="true"></i> Print
            </button>
            <button class="btn btn-primary mb-2" @click="exportToExcel">
              <span v-if="!excel.loading">
                <i class="fa fa-download" aria-hidden="true"></i>
                Export To Excel
              </span>
              <span v-else>
                <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                Loading
              </span>
            </button>
          </div>
        </div>
      </div>
      <div class="grid-body">
        <div class="row">
          <div class="col-md-4">
            <input
              type="text"
              class="form-control"
              placeholder="Search ..."
              @keyup.enter="search($event.target.value)"
            />
          </div>
          <div class="col-md-4">
            <button class="btn btn-primary" @click="showFilter()">
              <i class="fa fa-filter" aria-hidden="true"></i>
            </button>
          </div>
          <div class="col-md-4">
            <v-select
              :options="[
                'All',
                'Only fabrics with stock',
                'Only active fabrics',
                'Only available fabrics',
                'Future = keep',
              ]"
              @input="toggleActive"
              placeholder="Filter by status"
            ></v-select>
          </div>
        </div>
        <br />
        <div class="table-responsive">
          <table border="1">
            <thead>
              <tr v-if="columnKeys.length <= 0">
                <th :colspan="columnKeys.length">
                  <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                  Loading ....
                </th>
              </tr>
              <tr>
                <th
                  @click="sort(k.key)"
                  v-for="(k, index) in columnKeys"
                  :width="k.width"
                  :class="k.cl"
                  :key="index"
                  :align="k.align"
                >
                  {{ k.header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="fabric in fabrics">
                <tr v-if="!loading">
                  <td
                    v-for="(k, index) in columnKeys"
                    :class="k.cl"
                    :width="k.width"
                    :align="k.align"
                    :key="index"
                  >
                    <span v-if="k.rolls && fabric.rolls[k.id]">
                      {{ fabric.rolls[k.id].amount }} ({{
                        fabric.rolls[k.id].warehouse_name
                      }})
                    </span>
                    <span v-else>
                      <span
                        v-if="k.key == 'toggle'"
                        @click="toggleRolls(fabric)"
                      >
                        <i
                          v-if="fabric.show_rolls"
                          class="fa fa-chevron-down"
                        ></i>
                        <i v-else class="fa fa-chevron-right"></i>
                      </span>

                      <span v-else-if="k.key == 'image'" class="image">
                        <img :src="fabric.image_url" class="img" alt="" />
                      </span>

                      <span v-else-if="k.key == 'id'">
                        <router-link :to="'/fabric/update/' + fabric.id">
                          {{ $Fabric.getCode(fabric.id) }}
                        </router-link>
                      </span>

                      <span v-else-if="k.key == 'name'">
                        <router-link :to="'/fabric/update/' + fabric.id">
                          {{ fabric.name }}
                        </router-link>
                      </span>

                      <span v-else-if="k.key == 'supplier_name'">
                        <router-link
                          :to="
                            '/fabric/suppliers/update/' +
                            fabric.current_fabric_supplier_id
                          "
                        >
                          {{ fabric.supplier_name }}
                        </router-link>
                      </span>

                      <span v-else-if="k.key == 'available_stock'">
                        {{ ezy.comma(fabric[k.key]) }}
                      </span>

                      <span v-else-if="k.key == 'in_stock'">
                        {{ ezy.comma(fabric[k.key]) }}
                      </span>

                      <span v-else-if="k.key == 'status'">
                        <span
                          :class="
                            'badge badge-' + getStatusColor(fabric.status)
                          "
                        >
                          {{ fabric.status.replace(/_/g, " ") }}
                        </span>
                      </span>

                      <span v-else-if="k.key == 'order_necessary'">
                        <span
                          class="badge badge-danger"
                          v-if="fabric[k.key] == 'Yes'"
                        >
                          YES
                        </span>
                        <span v-else class="badge badge-primary"> NO </span>
                      </span>

                      <span v-else-if="k.key == 'placed_order'">
                        <router-link
                          :to="'/fabric/orders'"
                          class="badge badge-primary"
                          v-if="fabric[k.key] == 'Yes'"
                        >
                          YES
                        </router-link>
                        <span v-else class="badge badge-danger"> NO </span>
                      </span>

                      <span v-else>
                        {{ fabric[k.key] }}
                      </span>
                    </span>
                  </td>
                </tr>
                <tr v-if="!loading && fabric.show_rolls" class="rolls-table">
                  <td></td>
                  <td :colspan="columnKeys.length - 1" class="rolls-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Roll</th>
                          <th>Status</th>
                          <th>Warehouse</th>
                          <th>Available stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="roll in fabric.rolls" :key="roll.id">
                          <td>{{ roll.roll_code }}</td>
                          <td>{{ roll.status }}</td>
                          <td>{{ roll.warehouse_name }}</td>
                          <td>
                            {{
                              roll.unit == "y"
                                ? roll.available_stock +
                                  "y (" +
                                  roll.available_stock_meters +
                                  "m)"
                                : roll.available_stock_meters + "m"
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- print page -->
    <div v-else>
      <div class="row no-print">
        <div class="col-md-12">
          <button
            class="btn btn-danger no-print mb-3 float-right"
            @click="showInventoryView()"
          >
            Back
          </button>
        </div>
      </div>
      <div class="row print-page">
        <div class="col-sm-12" v-for="fabric in fabrics" :key="fabric.id">
          <div class="grid mb-2">
            <div class="grid-title p-2">
              <h5 class="text-bold">
                {{ $Fabric.getCode(fabric.id) }} ({{ fabric.name }}) - Total
                Rolls ( {{ fabric.rolls.length }} )
              </h5>
            </div>
            <div class="grid-body p-2" style="min-height: 30px">
              <span v-for="(roll, index) in fabric.rolls" :key="index">
                <button
                  class="btn btn-sm btn-white mr-2 mb-2 in-use"
                  v-if="roll.status == 'in_use'"
                >
                  {{ roll.amount }} ({{ roll.print_warehouse_name }})
                </button>

                <button
                  class="btn btn-sm btn-white mr-2 mb-2 up-stairs"
                  v-else-if="roll.status == 'up_stairs'"
                >
                  {{ roll.amount }} ({{ roll.print_warehouse_name }})
                </button>

                <button
                  class="btn btn-sm btn-white mr-2 mb-2 down-stairs"
                  v-else
                >
                  {{ roll.amount }} ({{ roll.print_warehouse_name }})
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- filter Modal -->
    <div
      class="modal fade"
      id="filter-modal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="modelTitleId"
      aria-hidden="true"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header bg-primary">
            <h5 class="modal-title text-white text-bold">Filters</h5>
          </div>
          <div class="modal-body bg-white p-20">
            <div class="row p-5 mb-20">
              <div class="col-md-4">Status</div>
              <div class="col-md-8">
                <span v-for="status in statuses" :key="status.key">
                  <p-radio
                    class="p-default"
                    :value="status.key"
                    name="status"
                    v-model="filters.status"
                    color="success"
                    checked
                  >
                    {{ status.title }}
                  </p-radio>
                  <br />
                </span>
              </div>
            </div>
            <div class="row p-5 mb-20">
              <div class="col-md-4">Order Necessary</div>
              <div class="col-md-8">
                <p-radio
                  class="p-default"
                  :value="null"
                  name="necessary"
                  v-model="filters.necessary"
                  color="success"
                  checked
                >
                  All
                </p-radio>
                <p-radio
                  class="p-default"
                  value="Yes"
                  name="necessary"
                  v-model="filters.necessary"
                  color="success"
                  checked
                >
                  Yes
                </p-radio>
                <p-radio
                  class="p-default"
                  value="No"
                  name="necessary"
                  v-model="filters.necessary"
                  color="success"
                  checked
                >
                  No
                </p-radio>
              </div>
            </div>
            <div class="row p-5 mb-20">
              <div class="col-md-4">Placed Order</div>
              <div class="col-md-8">
                <p-radio
                  class="p-default"
                  :value="null"
                  name="placed_order"
                  v-model="filters.placed_order"
                  color="success"
                  checked
                >
                  All
                </p-radio>
                <p-radio
                  class="p-default"
                  value="Yes"
                  name="placed_order"
                  v-model="filters.placed_order"
                  color="success"
                  checked
                >
                  Yes
                </p-radio>
                <p-radio
                  class="p-default"
                  value="No"
                  name="placed_order"
                  v-model="filters.placed_order"
                  color="success"
                  checked
                >
                  No
                </p-radio>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              Close
            </button>
            <button type="button" class="btn btn-primary" @click="filter()">
              Filter
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FabricCalculationInfo from "./dashboardComponents/FabricCalculationInfo"

export default {
  components: {
    FabricCalculationInfo
  },
  data() {
    return {
      print: false,
      activeStatusHandle: false,
      originalColumnKeys: [
        { key: "toggle", width: 35, header: "", align: "center" },
        { key: "image", width: 50, header: "Image", align: "center" },
        { key: "id", width: 50, header: "ID", align: "left" },
        { key: "name", width: 100, header: "Name", align: "left" },
        { key: "supplier_name", width: 130, header: "Supplier", align: "left" },
        {
          key: "available_stock",
          width: 80,
          header: "Total Stock",
          align: "center",
        },
        {
          key: "reserved_stock",
          width: 130,
          header: "Reserved Stock",
          align: "center",
        },
        { key: "in_stock", width: 80, header: "Free Stock", align: "center" },
        { key: "status", width: 200, header: "Status", align: "center" },
        {
          key: "out_of_stock_date",
          width: 130,
          header: "Sold Out Date",
          align: "center",
        },
        {
          key: "order_necessary",
          width: 130,
          header: "Order Necessary",
          align: "center",
        },
        {
          key: "placed_order",
          width: 130,
          header: "Placed Order",
          align: "center",
        },
        {
          key: "ordered_amount",
          width: 130,
          header: "Order Amount",
          align: "right",
        },
        { key: "delivery_date", width: 130, header: "ETA", align: "center" },
      ],
      columnKeys: [],
      fabrics: [],
      currentFabric: {},
      originalData: [],
      loading: false,
      sortType: "asc",
      excel: {
        loading: false,
      },
      filters: {
        status: null,
        necessary: null,
        placed_order: null,
      },
      statuses: [
        { key: null, title: "All" },
        { key: "available", title: "Available" },
        {
          key: "soon_sold_out_coming_back",
          title: "Soon sold out coming back",
        },
        {
          key: "soon_sold_out_not_coming_back",
          title: "Soon sold out not coming back",
        },
        { key: "sold_out_coming_back", title: "Sold out coming back" },
        { key: "sold_out_not_coming_back", title: "Sold out not coming back" },
        { key: "not_released_yet", title: "Not released yet" },
        { key: "inactive", title: "Inactive" },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async showPrintView() {
      if (!this.loadAllRolls) {
        try {
          let response = await axios.get(`v2/fabrics/rolls`);
          this.loadAllRolls = true;
          let allRolls = response.data.data;
          if (allRolls) {
            Object.keys(allRolls).forEach((fabricId) => {
              var existingFabric = this.fabrics.filter((f) => f.id == fabricId);
              if (existingFabric.length > 0) {
                existingFabric[0].rolls = allRolls[fabricId];
              }
            });
          }
        } catch (error) {
          if (error.response.status === 403) {
            toastr.error(
              "Not authorized, please ensure that you have the required permissions."
            );
          }
        }
      }
      this.print = true;
    },

    showInventoryView() {
      this.print = false;
    },

    getStatusColor(status) {
      if (["available"].includes(status)) {
        return "primary";
      } else if (
        ["soon_sold_out_not_coming_back", "soon_sold_out_coming_back"].includes(
          status
        )
      ) {
        return "warning";
      } else {
        return "danger";
      }
    },

    showFilter() {
      show_modal("#filter-modal");
    },

    toggleActive(value) {
      this.filters.status = value;
      this.filter();
    },

    filter() {
      let filteredFabrics = this.originalData;

      if (this.filters.status === null || this.filters.status === "inactive") {
        this.activeStatusHandle = false;
      }

      if (this.filters.status) {
        filteredFabrics = filteredFabrics.filter((fabric) => {
          if (this.filters.status === "All") {
            return fabric;
          }
          if (this.filters.status === "Only fabrics with stock") {
            return fabric.in_stock > 0;
          }
          if (this.filters.status === "Only active fabrics") {
            return (
              fabric.status === "available" ||
              fabric.status === "soon_sold_out_coming_back" ||
              fabric.status === "soon_sold_out_not_coming_back"
            );
          }
          if (this.filters.status === "Only available fabrics") {
            return fabric.status === "available";
          }
          if (this.filters.status === "Future = keep") {
            return fabric.future === "keep";
          }

          return fabric.status === this.filters.status;
        });
      }

      if (this.filters.necessary) {
        filteredFabrics = filteredFabrics.filter((fabric) => {
          return fabric.order_necessary === this.filters.necessary;
        });
      }

      if (this.filters.placed_order) {
        filteredFabrics = filteredFabrics.filter((fabric) => {
          return fabric.placed_order === this.filters.placed_order;
        });
      }

      this.fabrics = filteredFabrics;
      hide_modal("#filter-modal");
    },

    async toggleRolls(fabric) {
      if (fabric.show_rolls) {
        fabric.show_rolls = false;
      } else {
        if (!fabric.rolls_loaded) {
          try {
            let response = await axios.get(
              `v2/fabrics/${fabric.id}/rolls?filter=non_empty`
            );
            fabric.rolls_loaded = true;
            fabric.rolls = response.data.data;
          } catch (error) {
            if (error.response.status === 403) {
              toastr.error(
                "Not authorized, please ensure that you have the required permissions."
              );
            }
          }
        }

        this.fabrics.map((f) => {
          f.show_rolls = false;
        });

        fabric.show_rolls = true;
      }
    },

    showTableHeaders() {
      this.loading = true;
      this.rollsEnable = false;
      setTimeout(() => {
        this.columnKeys = this.originalColumnKeys;
      }, 300);
      setTimeout(() => {
        this.loading = false;
      }, 3000);
    },

    search(search) {
      if (search != "") {
        this.fabrics = this.originalData.filter((obj) => {
          return (
            ezy.lower(obj.name).includes(ezy.lower(search)) ||
            ezy.lower(obj.supplier_name).includes(ezy.lower(search)) ||
            $Fabric.getCode(obj.id) == search ||
            ezy.lower(obj.supplier_code) == ezy.lower(search)
          );
        });
      } else {
        this.fabrics = this.originalData;
      }
    },

    sort(column) {
      this.sortType = this.sortType === "asc" ? "desc" : "asc";
      if (column === "out_of_stock_date") {
        this.fabrics = _.orderBy(
          this.fabrics,
          ["oos_date_int"],
          [this.sortType]
        );
      } else {
        this.fabrics = _.orderBy(this.fabrics, [column], [this.sortType]);
      }
    },

    async getData() {
      try {
        let res = await axios.get("v2/fabrics/inventory");

        let fabrics = res.data.data.map((f) => {
          let outOfStockDate =
            f["out_of_stock_date"] > 0
              ? moment.unix(f["out_of_stock_date"]).format("DD MMM, YYYY")
              : "";

          if (f["out_of_stock_date"] < 0 || f["out_of_stock_date"] === null) {
            outOfStockDate = "No data";
          }

          f["oos_date_int"] = parseInt(f["out_of_stock_date"]);
          f["out_of_stock_date"] = outOfStockDate;
          f["delivery_date"] = ezy.date(f["delivery_date"]);
          f["in_stock"] = f.in_stock * 1;
          f["code"] = $Fabric.getCode(f.id);
          f["delivered_amount"] =
            f.delivered_amount <= 0 ? "" : ezy.comma(f.delivered_amount);
          f["ordered_amount"] =
            f.ordered_amount <= 0 ? "" : ezy.comma(f.ordered_amount);
          f["rolls"] = [];
          f["rolls_loaded"] = false;
          f["show_rolls"] = false;

          if (f["used_calculation_value"] && f["oos_date_int"] > 0) {
            f["out_of_stock_date"] =
              f["out_of_stock_date"] + " (" + f["used_calculation_value"] + ")";
          }

          // Select the image to display
          let images = f.images;
          if (images.length > 0) {
            // Use backend as main image
            let image = images.find((img) => {
              return img.type === "backend";
            });
            // Use the first image if the previous type is not found.
            if (!image) {
              image = images[0];
            }
            f["image_url"] = image.image_url;
          } else {
            f["image_url"] = "/assets/img/fabric-placeholder.png";
          }

          return f;
        });
        this.fabrics = fabrics;
        this.originalData = fabrics;
        this.showTableHeaders();
      } catch (error) {
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        }
      }
    },

    async exportToExcel() {
      if (await ezy.confirm("Are You Sure To Export?", "YES")) {
        try {
          let res = await axios
            .get("v2/fabrics/inventory/export", { responseType: "blob" })
            .then((response) => {
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement("a");
              link.href = url;

              let fileName =
                "FabricInventoryCheck" + moment().format("YYYY-MM-DD");

              link.setAttribute("download", `${fileName}.xlsx`);
              document.body.appendChild(link);
              link.click();
            });
        } catch (error) {
          if (error.response.status === 403) {
            toastr.error(
              "Not authorized, please ensure that you have the required permissions."
            );
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
th {
  text-align: center;
}
.mb-2 {
  margin-bottom: 5px;
}
h4 {
  margin-top: 7px;
}
table {
  table-layout: fixed !important;
  max-width: none;
  width: 100%;
  border-collapse: separate !important; /* Don't collapse */
  border-spacing: 0 !important;
}

th,
td {
  padding: 5px !important;
  border: 1px solid #d7d8da;
  border-left: none;
  border-top: none;
}
td:last-child,
th:last-child {
  border-right: none;
}

.table-responsive {
  height: 700px;
  overflow: scroll;
}
th {
  position: sticky;
  top: -1px;
  z-index: 1;
  background: var(--primary-color);
  color: white;
}

@media print {
  @page {
    size: "A4"; /* landscape */
    margin: 4mm;
  }
  .no-print,
  .no-print * {
    display: none !important;
  }
  .print-page {
    margin-top: -100px;
  }
  .page-sidebar {
    display: none !important;
  }
  .print-page {
    .btn {
      box-shadow: none !important;
    }
    .grid {
      box-shadow: none !important;
      border: 1px solid gray !important;
      padding: 2px !important;
    }
  }
}
.cell {
  float: left;
}
.btn-sm {
  padding: 2px 5px !important;
}
.in-use {
  border-radius: 0px !important;
  font-weight: bold;
}
.up-stairs {
  border: 2px solid gray;
}
.down-stairs {
  border-radius: 20px;
  padding: 2px 7px !important;
}
.toggle {
  padding-top: 8px !important;
}
.rolls-table {
  background-color: #f5f5f5;
}

.rolls-table table {
  background-color: #fff;
}
.rolls-table .rolls-container {
  padding: 0 !important;
}
.rolls-table table tr:nth-child(even) {
  background-color: #f9f9f9;
}
.image img {
  width: 50px;
  margin-left: -5px;
}
.fabric-calculation-info-container {
  display: inline-block;
  margin-left: 5px;
  padding: 6px;
  border-radius: 8px;
}
</style>
